import React from 'react';
import { FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useFindInviteFormController } from './FindInviteForm.controller';
import { StyledContent, StyledFormStack, StyledHeaderStack, StyledInputsStack, StyledTitleStack, styles } from '../UnlockForm/UnlockForm.styles';
import { StyledInputWrapper } from '../shared/UnlockInputWrapper.styles';
import type { GuestTypes } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, hed6ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useDeferredInputFocus } from '../useDeferredInputFocus';
import { useUnlockDialogInputStyles } from '../useUnlockDialogButtonStyles';
import { UnlockSubmitButton } from '../shared/UnlockSubmitButton';

export interface FindInviteFormProps {
  eventId: string;
  setGuestsFound: (p: number) => void;
  setGuest: (p: GuestTypes) => void;
}

export const FindInviteForm: React.FC<FindInviteFormProps> = ({ eventId, setGuestsFound, setGuest }) => {
  const { formik, loading, title, subtitle, firstNameLabel, lastNameLabel, firstNamePlaceholder, lastNamePlaceholder, submitButtonText, lastNameInputError, firstNameInputError } =
    useFindInviteFormController({ eventId, setGuestsFound, setGuest });
  const inputRef = useDeferredInputFocus();
  const inputStyles = useUnlockDialogInputStyles();

  return (
    <StyledContent
      __css={styles.content}
      data-testid="find-invite-form"
    >
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2 typographyVariant="hed6">{title}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2 typographyVariant="body4">{subtitle}</TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={firstNameLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  ref={inputRef}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={firstNamePlaceholder}
                  isInvalid={(firstNameInputError && formik.touched.firstName) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('firstName')}
                />
              </FormControl>
            </StyledInputWrapper>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={lastNameLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={lastNamePlaceholder}
                  isInvalid={(lastNameInputError && formik.touched.lastName) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('lastName')}
                />
              </FormControl>
            </StyledInputWrapper>
          </StyledInputsStack>
          <UnlockSubmitButton
            width={'100%'}
            shape="rounded"
            variant="outline"
            type="submit"
            loading={loading}
          >
            {submitButtonText}
          </UnlockSubmitButton>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
